import {
  areFieldsDirty,
  buildAllFieldsFromSections,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  Card,
  CardFooter,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Form,
  FormBuilderSections,
  getResolver,
  toast,
  resolveSectionsFieldShortcuts,
} from '@folio/components';
import {
  Asset,
  BranchDto,
  BrokerageFeeOnPolicy,
  clientWithFork,
  EditPolicyInputBody,
  getNewBranchIdAfterFork,
  Policy,
  useAssetControllerEditAsset,
  useDerogationControllerCreateDerogation,
  usePolicyControllerEditPolicy,
  usePolicyControllerSetPolicyFees,
  configControllerGetPublicProductConfigQueryOptions,
  QuoteConfig,
} from 'korint-sdk';
import { Fields, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { Loader2Icon } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function WaitingForQuoteCard({
  policy,
  asset,
  assets,
  branch,
  quoteConfig,
  policySections: rawPolicySections,
  assetSections: rawAssetSections,
}: {
  policy: Policy;
  asset: Asset;
  assets: Asset[];
  branch: BranchDto;
  quoteConfig: QuoteConfig;
  policySections: FormSectionsWithShortcuts;
  assetSections: FormSectionsWithShortcuts;
}) {
  const navigate = useNavigate();
  const assetIds = useMemo(() => assets.map(asset => asset.id), [assets]);

  const { mutateAsync: updatePolicy } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updatePolicyFees } = usePolicyControllerSetPolicyFees({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updateAsset } = useAssetControllerEditAsset({
    client: { client: clientWithFork },
  });

  const { mutateAsync: createDerogation, isPending } = useDerogationControllerCreateDerogation({
    mutation: {
      onSuccess: () => {
        toast({
          title: 'Demande de vérification envoyée',
          description: "Votre demande de vérification a été transmise à l'équipe.",
        });
      },
    },
  });

  const { data: { policyFields: policyFieldsConfig, assetFields: assetFieldsConfig } = {} } =
    useQuery({
      ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    });

  const policySections = useMemo(
    () => resolveSectionsFieldShortcuts(rawPolicySections, policyFieldsConfig ?? []),
    [rawPolicySections, policyFieldsConfig],
  );
  const policyFields = useMemo(() => buildAllFieldsFromSections(policySections), [policySections]);

  const assetSections = useMemo(
    () =>
      resolveSectionsFieldShortcuts(rawAssetSections, assetFieldsConfig ?? [])
        .map(section => ({
          ...section,
          fields: section.fields.filter(
            field => field.name !== 'startedAt' || field.disabled || asset?.createdOnBranch,
          ),
        }))
        .filter(section => section.fields.some(f => !f.hidden)),
    [rawAssetSections, assetFieldsConfig, asset?.createdOnBranch],
  );
  const assetFields = useMemo(() => buildAllFieldsFromSections(assetSections), [assetSections]);

  const form = useForm({
    mode: 'all',
    values: {
      tier: asset?.quote?.options?.tier ?? quoteConfig?.defaultConfig.tier,
      excess: asset?.quote?.options?.excess ?? quoteConfig?.defaultConfig.excess,
      ...buildFormValuesFromFields(policyFields, policy),
      ...buildFormValuesFromFields(assetFields, asset),
    } as Record<string, unknown>,
    resolver: getResolver([...policyFields, ...assetFields]),
  });

  const handleFieldsUpdate = async (
    policyFields: Fields,
    assetFields: Fields,
    data: Record<string, unknown>,
    dirtyFields: Record<string, boolean | undefined>,
  ) => {
    let mutationBranchId: string = branch.id;

    if (policyFields.length > 0 && areFieldsDirty(policyFields, dirtyFields)) {
      const { fees, ...policyBody } = buildPayloadFromFields(
        policyFields,
        data,
        dirtyFields,
      ) as Partial<EditPolicyInputBody> & { fees?: BrokerageFeeOnPolicy[] };

      if (!isEmpty(policyBody)) {
        const response = await updatePolicy({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: policyBody,
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }

      // updatePolicyFees doesn't have the same restrictions as updatePolicy on QUOTED status,
      // and thus must be perform after instead of before to avoid losing updates on fork
      if (fees !== undefined) {
        const response = await updatePolicyFees({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { fees },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    if (assetFields.length > 0 && areFieldsDirty(assetFields, dirtyFields)) {
      for (const assetId of assetIds) {
        const response = await updateAsset({
          assetId,
          params: { branchId: mutationBranchId },
          data: buildPayloadFromFields(assetFields, data, dirtyFields),
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    return mutationBranchId;
  };

  const onSubmit = form.handleSubmit(async data => {
    const { dirtyFields } = form.formState;
    const mutationBranchId = await handleFieldsUpdate(policyFields, assetFields, data, dirtyFields);

    await createDerogation({
      branchId: mutationBranchId,
      params: { action: 'QUOTE' },
    });

    navigate(`/policies/${policy.id}/${mutationBranchId}`);
  });

  return (
    <Form {...form}>
      <form className="flex flex-col gap-4" onSubmit={e => e.preventDefault()}>
        {assetSections.length > 0 && (
          <FormBuilderSections sections={assetSections} externalData={asset} />
        )}
        {policySections.length > 0 && (
          <FormBuilderSections
            sections={policySections}
            externalData={{ ...policy, asset, branchId: branch?.id }}
          />
        )}
        <Stack>
          <Card>
            <CardHeader>
              <CardTitle>Devis en attente de vérification</CardTitle>
            </CardHeader>
            <CardContent>
              <Text tone="muted">
                Veuillez faire une demande de dérogation pour avoir un devis, vous pourrez suivre
                son avancement dans l'onglet "Evénements" de votre tableau de bord.
              </Text>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button
                onClick={onSubmit}
                disabled={isPending || form.formState.isSubmitting}
                loading={isPending || form.formState.isSubmitting}
                className="flex items-center gap-2"
              >
                {(isPending || form.formState.isSubmitting) && (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                )}
                Demander une vérification
              </Button>
            </CardFooter>
          </Card>
        </Stack>
      </form>
    </Form>
  );
}
